/**
 * Media Session bridge — TASK 8.3 Step 4 (AC 6.6, AC 6.9).
 *
 * Publishes the now-playing metadata and the transport handlers to the OS, so
 * the lock screen, the notification shade, a car head unit and a Bluetooth
 * remote can all drive playback. Everything here is BEST-EFFORT by contract
 * (AC 6.9): the API is absent on some engines, and Safari rejects individual
 * actions it does not implement by THROWING from `setActionHandler`. So:
 *
 *   * the whole module no-ops when `navigator.mediaSession` is missing;
 *   * every action is registered inside its own try/catch, and one refusal
 *     never prevents the others from being registered — which is the entire
 *     point, because on iOS the actions that survive are the ones the
 *     lock-screen controls actually use;
 *   * {@link MediaSessionController.registeredActions} reports what actually
 *     took, so a caller (and a test) can tell "registered" from "asked".
 *
 * IN-APP CONTROLS NEVER DEPEND ON ANY OF THIS. The mini and full players call
 * the engine directly; this module only adds an out-of-app surface.
 *
 * @module
 */

/** The actions this app publishes, in the order they are registered. */
export const MEDIA_SESSION_ACTIONS = [
  // `nexttrack` FIRST, deliberately — see TASK 8.4 and the iOS spike report:
  // it is the guaranteed lock-screen path when background auto-advance is
  // unreliable, so it must not be starved by an earlier action throwing.
  'nexttrack',
  'previoustrack',
  'play',
  'pause',
  'stop',
  'seekbackward',
  'seekforward',
  'seekto',
] as const;

export type MediaSessionAction = (typeof MEDIA_SESSION_ACTIONS)[number];

export interface MediaSessionMetadataInput {
  readonly title: string;
  readonly artist: string;
  readonly album: string;
  readonly artwork?: ReadonlyArray<{ src: string; sizes?: string; type?: string }>;
}

export interface MediaSessionHandlers {
  readonly play?: () => void;
  readonly pause?: () => void;
  readonly stop?: () => void;
  readonly nexttrack?: () => void;
  readonly previoustrack?: () => void;
  readonly seekbackward?: (offsetSeconds: number) => void;
  readonly seekforward?: (offsetSeconds: number) => void;
  readonly seekto?: (seconds: number) => void;
}

export interface MediaSessionController {
  /** False when the runtime has no Media Session at all. */
  readonly supported: boolean;
  /** Which actions the runtime ACCEPTED — not merely which were offered. */
  readonly registeredActions: readonly MediaSessionAction[];
  /**
   * TASK 8.4: is the GUARANTEED iOS path in place? On an engine whose
   * background auto-advance is only best-effort, this being `false` means the
   * user has no reliable way to reach the next track from a locked screen.
   */
  readonly nextTrackRegistered: boolean;
  /** What this engine can promise for BACKGROUND auto-advance (TASK 8.4). */
  readonly autoAdvanceTier: AutoAdvanceTier;
  setHandlers(handlers: MediaSessionHandlers): void;
  setMetadata(metadata: MediaSessionMetadataInput | null): void;
  setPlaybackState(state: 'none' | 'paused' | 'playing'): void;
  setPositionState(state: { duration: number; position: number; playbackRate?: number }): void;
  /** Unregisters every handler and clears the metadata. */
  dispose(): void;
}

/** The slice of `navigator.mediaSession` this module touches. */
export interface MediaSessionLike {
  metadata: unknown;
  playbackState?: string;
  setActionHandler(action: string, handler: ((details: unknown) => void) | null): void;
  setPositionState?(state: {
    duration: number;
    position: number;
    playbackRate: number;
  }): void;
}

/**
 * TASK 8.4 — the two tiers the dev plan defines for BACKGROUND auto-advance
 * (the `ended` -> load-next chain while the app is backgrounded or the screen
 * is locked):
 *
 *   `guaranteed`  the engine keeps timers and media events running well
 *                 enough that the chain is a product promise (Chromium,
 *                 Firefox, and desktop Safari);
 *   `best-effort` iOS/iPadOS, where a backgrounded PWA is aggressively
 *                 suspended and the chain may not fire. The GUARANTEED path
 *                 there is the lock-screen `nexttrack` control, which the OS
 *                 delivers to a suspended page.
 *
 * The tier is a STATEMENT OF WHAT WE PROMISE, not a behaviour switch: the
 * engine attempts auto-advance identically everywhere. See
 * docs/dev_plans/ios-spike-report.md — the Phase-1 device spike that would
 * PROMOTE iOS to `guaranteed` has not been executed (no physical device in
 * the build environment), so iOS stays at the conservative tier and the
 * fallback ships unconditionally, which costs nothing if the spike later
 * passes.
 */
export type AutoAdvanceTier = 'guaranteed' | 'best-effort';

/** The `navigator` fields the engine sniff reads. */
export interface EngineHints {
  readonly userAgent?: string;
  readonly platform?: string;
  readonly maxTouchPoints?: number;
}

/**
 * Is this WebKit-on-iOS? Every iOS browser is, whatever its badge, because
 * the platform forbids other engines — so the UA's browser name is useless
 * and the PLATFORM is what matters.
 *
 * iPadOS 13+ reports itself as "MacIntel"/Macintosh, so the touch-point count
 * is the disambiguator: a real Mac reports 0.
 */
export function isIosLikeEngine(hints: EngineHints | null | undefined): boolean {
  if (hints === null || hints === undefined) return false;
  const ua = hints.userAgent ?? '';
  const platform = hints.platform ?? '';
  if (/iPhone|iPad|iPod/i.test(ua) || /iPhone|iPad|iPod/i.test(platform)) return true;
  // iPadOS masquerading as a desktop Mac.
  const macLike = /Macintosh|MacIntel/i.test(`${ua} ${platform}`);
  return macLike && (hints.maxTouchPoints ?? 0) > 1;
}

/** {@link AutoAdvanceTier} for an engine. */
export function autoAdvanceTier(hints: EngineHints | null | undefined): AutoAdvanceTier {
  return isIosLikeEngine(hints) ? 'best-effort' : 'guaranteed';
}

export interface MediaSessionDeps {
  /** Defaults to `navigator.mediaSession`; pass `null` to force the no-op. */
  readonly session?: MediaSessionLike | null;
  /** Defaults to `window.MediaMetadata`; absent means metadata is skipped. */
  readonly metadataCtor?: (new (init: Record<string, unknown>) => unknown) | null;
  /** Diagnostics sink; silent by default (a refusal is expected, not a fault). */
  readonly onUnsupportedAction?: (action: MediaSessionAction) => void;
  /** Engine sniff source; defaults to `navigator`. */
  readonly hints?: EngineHints | null;
}

function defaultSession(): MediaSessionLike | null {
  if (typeof navigator === 'undefined') return null;
  const candidate = (navigator as { mediaSession?: MediaSessionLike }).mediaSession;
  return candidate ?? null;
}

function defaultMetadataCtor(): (new (init: Record<string, unknown>) => unknown) | null {
  const candidate = (globalThis as { MediaMetadata?: new (init: Record<string, unknown>) => unknown })
    .MediaMetadata;
  return candidate ?? null;
}

/**
 * Builds the DOM listener for one action, unwrapping the two payload shapes
 * the spec defines. Written as a switch rather than an index lookup so the
 * numeric-argument actions narrow properly instead of needing a cast.
 */
function listenerFor(
  action: MediaSessionAction,
  handlers: MediaSessionHandlers,
): ((details: unknown) => void) | null {
  switch (action) {
    case 'seekto': {
      const fn = handlers.seekto;
      if (fn === undefined) return null;
      return (details) => {
        fn((details as { seekTime?: number } | undefined)?.seekTime ?? 0);
      };
    }
    case 'seekbackward':
    case 'seekforward': {
      const fn = action === 'seekbackward' ? handlers.seekbackward : handlers.seekforward;
      if (fn === undefined) return null;
      return (details) => {
        // The spec's own default nudge when the OS omits an offset.
        fn((details as { seekOffset?: number } | undefined)?.seekOffset ?? 10);
      };
    }
    default: {
      const fn = handlers[action];
      if (fn === undefined) return null;
      return () => {
        fn();
      };
    }
  }
}

export function createMediaSession(deps: MediaSessionDeps = {}): MediaSessionController {
  const session = deps.session === undefined ? defaultSession() : deps.session;
  const metadataCtor =
    deps.metadataCtor === undefined ? defaultMetadataCtor() : deps.metadataCtor;
  const onUnsupportedAction = deps.onUnsupportedAction ?? (() => undefined);
  const hints =
    deps.hints === undefined ? (typeof navigator === 'undefined' ? null : navigator) : deps.hints;
  const tier = autoAdvanceTier(hints);

  /** @type {MediaSessionAction[]} */
  const registered: MediaSessionAction[] = [];

  const controller: MediaSessionController = {
    get supported() {
      return session !== null;
    },

    get registeredActions() {
      return [...registered];
    },

    get nextTrackRegistered() {
      return registered.includes('nexttrack');
    },

    get autoAdvanceTier() {
      return tier;
    },

    setHandlers(handlers) {
      if (session === null) return;
      registered.length = 0;

      for (const action of MEDIA_SESSION_ACTIONS) {
        try {
          session.setActionHandler(action, listenerFor(action, handlers));
          if (handlers[action] !== undefined) registered.push(action);
        } catch {
          // Safari throws `NotSupportedError` per unimplemented action. That is
          // an expected outcome, not a fault: skip it and keep going, so one
          // refusal cannot cost us the actions that DO work.
          onUnsupportedAction(action);
        }
      }
    },

    setMetadata(metadata) {
      if (session === null) return;
      if (metadata === null || metadataCtor === null) {
        session.metadata = null;
        return;
      }
      try {
        session.metadata = new metadataCtor({
          title: metadata.title,
          artist: metadata.artist,
          album: metadata.album,
          artwork: metadata.artwork === undefined ? [] : [...metadata.artwork],
        });
      } catch {
        // A metadata failure must never break playback.
        session.metadata = null;
      }
    },

    setPlaybackState(state) {
      if (session === null) return;
      try {
        session.playbackState = state;
      } catch {
        /* read-only in some engines — nothing to do */
      }
    },

    setPositionState(state) {
      if (session === null || typeof session.setPositionState !== 'function') return;
      // The spec THROWS a TypeError for a non-finite duration, a duration of 0,
      // or a position past the duration — all of which are normal while a
      // track is still loading. Guard rather than catch, so a real bug is not
      // hidden behind an empty catch block.
      const { duration, position, playbackRate = 1 } = state;
      if (!Number.isFinite(duration) || duration <= 0) return;
      if (!Number.isFinite(position) || position < 0 || position > duration) return;
      try {
        session.setPositionState({ duration, position, playbackRate });
      } catch {
        /* best-effort by contract (AC 6.9) */
      }
    },

    dispose() {
      if (session === null) return;
      for (const action of MEDIA_SESSION_ACTIONS) {
        try {
          session.setActionHandler(action, null);
        } catch {
          /* it was never registered */
        }
      }
      registered.length = 0;
      session.metadata = null;
    },
  };

  return controller;
}

/**
 * The process-wide controller. `modules/app/index.js#startApp` wires it to the
 * engine, which is what gives this module a production call site.
 */
export const mediaSession: MediaSessionController = createMediaSession();
